<template>
  <div class="app-dropdown-button" :class="{ 'add': add }">
    <div class="app-dropdown-button-moving" :class="{ 'add': add }">
      <app-button
        :cls="'app-dropdown-button-toggle ' + cls + (nocaret ? ' no-caret' : '')"
        :variant="variant"
        :disabled="isDisabled"
        :size="size"
        :loading="loading"
        @click="onClick"
        :noborder="noborder"
        :active="active"
        :superAdminProtected="superAdminProtected"
      >
        <slot name="title">{{ title }}</slot>
      </app-button>
      <ul class="app-dropdown-button-menu" :class="'align-' + align + (size ? ' size-' + size : '') + ' placement-' + placement">
        <slot />
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { Prop, Component } from "vue-property-decorator";
import AppButton from "../app-button/app-button.vue";
import { BaseComponent } from "../base-component/base-component";
import { CatchError } from "../../services/helper/catch-helper";

@Component({
  name: "app-dropdown-button",
  components: {
    AppButton,
  }
})
export default class AppDropdownButton extends BaseComponent {
  @Prop({ default: "" }) title!: string;
  @Prop({ default: "" }) cls!: string;
  @Prop({ default: "primary" }) variant!: string;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: "" }) size!: string;
  @Prop({ default: "left" }) align!: "left" | "right";
  @Prop({ default: false }) nocaret!: boolean;
  @Prop({ default: false }) noborder!: boolean;
  @Prop({ default: "bottom" }) placement!: "bottom" | "top";
  @Prop({ default: false }) add!: boolean
  @Prop({ default: false }) superAdminProtected!: boolean;

  active = false;

  private movedDropDown: HTMLElement | null = null;
  private clonedPlaceholderDropDown: HTMLElement | null = null;
  
  get isDisabled() {
    return this.loading || this.disabled;
  }

  @CatchError()
  onClick() {
    this.active = !this.active;

    if (this.active) {
      if (!this.movedDropDown) {
        this.clonedPlaceholderDropDown = this.$el.firstChild!.cloneNode(true) as HTMLElement;

        const rect = this.$el.getBoundingClientRect();

        // Move into body to make sure it overflows everything
        this.movedDropDown = document.body.appendChild(this.$el.firstChild!) as HTMLElement;
        this.movedDropDown.classList.add("onbody");
        this.movedDropDown.style.left = (rect.x + window.scrollX) + "px";
        this.movedDropDown.style.top = (rect.y + window.scrollY) + "px";
        (this.movedDropDown.children[1] as HTMLElement).style.top = (this.movedDropDown.children[0].clientHeight + 1) + "px";
        AppDropdownButton.fitMenuIntoViewport(this.movedDropDown.children[1] as HTMLElement);

        this.$el.appendChild(this.clonedPlaceholderDropDown);
      }
      
      // need to use settimeout. Otherwise onclick gets raised immediately again...
      setTimeout(() => {
        document.addEventListener("click", this.onClick);
      }, 1);
    } else {
      if (this.movedDropDown) {
        this.$el.removeChild(this.clonedPlaceholderDropDown!);
        this.clonedPlaceholderDropDown = null;

        this.movedDropDown.style.left = "unset";
        this.movedDropDown.style.top = "unset";
        this.movedDropDown.classList.remove("onbody");
        (this.movedDropDown.children[1] as HTMLElement).style.top = "unset";
        (this.movedDropDown.children[1] as HTMLElement).style.left = "";
        (this.movedDropDown.children[1] as HTMLElement).style.right = "";
        this.$el.appendChild(this.movedDropDown); // Move it back 

        this.movedDropDown = null;
      }

      document.removeEventListener("click", this.onClick);
    }
  }

  /** A menu aligned to a button near the edge of a narrow screen would reach out of it. */
  private static fitMenuIntoViewport(menu: HTMLElement) {
    const margin = 8;
    const rect = menu.getBoundingClientRect();
    let shift = Math.min(0, window.innerWidth - margin - rect.right);
    if (rect.left + shift < margin) {
      shift = margin - rect.left;
    }
    if (!shift) {
      return;
    }

    const parentLeft = (menu.offsetParent ?? document.body).getBoundingClientRect().left;
    menu.style.left = (rect.left + shift - parentLeft) + "px";
    menu.style.right = "auto";
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-dropdown-button {
  position: relative;
  display: inline-block;

  &.add {
    margin-left: -1px;
    .app-button {
      width: 30px;
      padding-left: 5px; 
      padding-right: 10px;
      border-left: none;
    }
  }
}

.app-dropdown-button-toggle::after {
  display: inline-block;
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0.3em solid;
  border-right: 0.3em solid rgba(0, 0, 0, 0);
  border-bottom: 0;
  border-left: 0.3em solid rgba(0, 0, 0, 0);
  color: var(--secondary-btn-text-color);
}
.app-dropdown-button-toggle.btn-primary::after {
  color: var(--primary-text-color);
}

.app-dropdown-button-moving.onbody {
  position: absolute;
  z-index: 1000;

  &.add {
    .app-button {
      width: 30px;
      padding-left: 5px; 
      padding-right: 10px;
      border-left: none;
    }
  }

  .app-dropdown-button-menu {
    display: block;
  }
}

.app-dropdown-button-menu {
  position: absolute;
  top: 0;
  display: none;
  min-width: 10rem;
  max-width: calc(100vw - 16px);
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  font-size: 1rem;
  text-align: left;
  list-style: none;
  background-color: var(--default-bg-color);
  border: 1px solid var(--default-border-color);

  &.align-left {
    left: 0;
  }
  &.align-right {
    right: 0;
  }
  &.size-xs {
    font-size: 14px;
    padding: 3px;
  }
  &.placement-top {
    top: -2px !important;
    -webkit-transform: translateY(-100%);
    transform: translateY(-100%);
  }
}
</style>
